import { Component, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import {
  BackButtonComponent,
  PageHeaderComponent,
} from '../../shared/components/ui-shell.component';
import { DatDetailCardComponent } from '../../shared/components/dat-detail-card.component';
import { MonitoringService } from '../../services/monitoring.service';
import { DatSegmentDetail } from '../../models/monitoring.models';
import { HistoryDay, parseHistoryDay } from '../../utils/history-ranges';

@Component({
  selector: 'app-dat-detail',
  standalone: true,
  imports: [PageHeaderComponent, BackButtonComponent, DatDetailCardComponent],
  template: `
    <div class="screen">
      <header class="top-bar">
        <app-page-header title="Détail DAT" [subtitle]="stationName()" />
        <button
          type="button"
          class="btn-refresh"
          [class.spinning]="refreshing()"
          [disabled]="refreshing()"
          title="Actualiser"
          (click)="refresh()"
        >↻</button>
      </header>

      <section class="content">
        <app-dat-detail-card [detail]="detail()" />
      </section>

      <app-back-button [backFn]="goBack.bind(this)" />
    </div>
  `,
  styles: [
    `
      .screen {
        min-height: 100%;
        display: flex;
        flex-direction: column;
      }

      .top-bar {
        position: relative;
      }

      .btn-refresh {
        position: absolute;
        top: 1rem;
        right: 1rem;
        width: 2.25rem;
        height: 2.25rem;
        border-radius: 50%;
        border: 1px solid #888;
        background: #fff;
        font-size: 1.25rem;
        cursor: pointer;
        line-height: 1;
      }

      .btn-refresh:disabled {
        cursor: default;
        opacity: 0.6;
      }

      .btn-refresh.spinning {
        animation: spin 0.8s linear infinite;
      }

      @keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }

      .content {
        flex: 1;
        padding: 1.5rem 1rem;
        display: flex;
        align-items: flex-start;
        justify-content: center;
      }
    `,
  ],
})
export class DatDetailComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly monitoring = inject(MonitoringService);

  readonly detail = signal<DatSegmentDetail | null>(null);
  readonly stationName = signal('');
  readonly refreshing = signal(false);
  private savedTime: number | null = null;
  private savedDay: HistoryDay = 'today';
  private stationId = '';
  private datId = '';
  private start = 0;

  constructor() {
    combineLatest([this.route.paramMap, this.route.queryParamMap])
      .pipe(
        map(([params, query]) => ({
          stationId: params.get('stationId') ?? '',
          datId: params.get('datId') ?? '',
          start: Number(query.get('start') ?? 0),
          time: query.get('time'),
          day: parseHistoryDay(query.get('day')),
        })),
      )
      .subscribe(({ stationId, datId, start, time, day }) => {
        if (time) {
          this.savedTime = Number(time);
        }
        this.savedDay = day;
        this.stationId = stationId;
        this.datId = datId;
        this.start = start;
        this.monitoring.getStation(stationId).subscribe((station) => {
          this.stationName.set(station?.name ?? stationId);
        });
        this.monitoring.getDatDetail(stationId, datId, start, day).subscribe((detail) => {
          this.detail.set(detail ?? null);
        });
      });
  }

  refresh(): void {
    if (this.refreshing()) {
      return;
    }
    this.refreshing.set(true);
    this.monitoring
      .getDatDetail(this.stationId, this.datId, this.start, this.savedDay)
      .pipe(finalize(() => this.refreshing.set(false)))
      .subscribe((detail) => this.detail.set(detail ?? null));
  }

  goBack(): void {
    const stationId = this.route.snapshot.paramMap.get('stationId') ?? '';
    const queryParams: Record<string, string | number> = { day: this.savedDay };
    if (this.savedTime != null) {
      queryParams['time'] = this.savedTime;
    }
    this.router.navigate(['/gare', stationId, 'dats'], { queryParams });
  }
}
